import React, { forwardRef, ReactNode } from 'react';
import styled from 'styled-components';
import {
  IFilterFieldsFragment,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import {
  HighlightedEntity,
  useHighlightingContext,
} from '../../HighlightingContext';
import {
  BuilderRowActions,
  RowActionsMenu,
} from '../../Builder/BuilderActions';
import { useActionsVisibilityContext } from '../../ActionsVisibilityContext';
import BasicFilterView from './BasicFilterView';
import { NamedFilterLabel } from '../../EntityLabels';
import PermissionElementWrapper from '../../../pages/PermissionControls/PermissionElementWrapper';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { IRowMeta } from '../../../redux/slices/oldBuilder';
import { SingleSelectOption } from '../../Dropdown/SingleSelect';
import { SquareRemoveButton } from '../../Button';

interface IProps {
  rowMeta?: IRowMeta;
  filter: IFilterFieldsFragment;
  onEdit: () => void;
  onRemove: () => void;
  schemaId: string;
  onExpandNamedFilter?: () => void;
  extraActions?: ReactNode;
}

const Container = styled(HighlightedEntity)`
  position: relative;
  display: flex;
  flex-basis: 100%;
  gap: 4px;
  min-height: 32px;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 2px;
  border-radius: 4px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 4px;
  row-gap: 6px;
`;

const FilterFieldsView = forwardRef<HTMLDivElement, IProps>(
  (
    {
      rowMeta,
      filter,
      extraActions,
      onEdit,
      onRemove,
      onExpandNamedFilter,
      schemaId,
    },
    ref,
  ) => {
    const { checkIsPermitted } = usePermission();
    const isPermittedToReadField = checkIsPermitted({
      requiredPermissionKeys: [PermissionKey.FieldRead],
      schemaId,
    });
    const { actionsVisible } = useActionsVisibilityContext();
    const { activateHighlighting, deactivateHighlighting } =
      useHighlightingContext();

    return (
      <Container>
        <Wrapper ref={ref}>
          {filter.__typename === 'NamedFilter' ? (
            <NamedFilterLabel
              namedFilter={filter}
              onExpandNamedFilter={onExpandNamedFilter}
              backgroundColor="transparent"
            />
          ) : (
            <BasicFilterView rowMeta={rowMeta} filter={filter} />
          )}
        </Wrapper>
        <BuilderRowActions>
          {actionsVisible && (
            <>
              <RowActionsMenu>
                <PermissionElementWrapper isPermitted={isPermittedToReadField}>
                  <SingleSelectOption
                    label={'Edit Filter'}
                    onClick={onEdit}
                    onMouseEnter={activateHighlighting}
                    onMouseLeave={deactivateHighlighting}
                    disabled={!isPermittedToReadField}
                  />
                </PermissionElementWrapper>
                {extraActions}
              </RowActionsMenu>
              <SquareRemoveButton
                onClick={onRemove}
                onMouseEnter={activateHighlighting}
                onMouseLeave={deactivateHighlighting}
                content={'Remove Filter'}
              />
            </>
          )}
        </BuilderRowActions>
      </Container>
    );
  },
);

export default FilterFieldsView;
